/* Created by Interactive 63 - https://interactive63.com */
/**
 * Undo and redo history for project snapshots. Snapshots share structure through Immer, so
 * keeping many of them is cheap. Rapid edits that share a coalesce key merge into one entry, and
 * keys that start with `drag:` merge however long the pointer rests, so a drag is one step.
 */
import type { Project } from '../../core/model/types';

export const MAX_HISTORY_ENTRIES = 200;
export const COALESCE_WINDOW_MS = 1000;

interface UndoEntry {
  label: string;
  /** The project as it was before the change. */
  project: Project;
  coalesceKey?: string;
  /** Time of the latest update merged into this entry. */
  time: number;
}

interface RedoEntry {
  label: string;
  /** The project as it was after the change. */
  project: Project;
}

export class History {
  private undoStack: UndoEntry[] = [];
  private redoStack: RedoEntry[] = [];

  /**
   * Records a change made from the `before` snapshot. A change with the same coalesce key as the
   * previous one, made within the coalescing window, merges into the previous entry. Drag keys
   * are unique per drag and have no window.
   */
  record(label: string, before: Project, now: number, coalesceKey?: string): void {
    this.redoStack = [];
    const top = this.undoStack[this.undoStack.length - 1];
    if (
      top &&
      coalesceKey !== undefined &&
      top.coalesceKey === coalesceKey &&
      (coalesceKey.startsWith('drag:') || now - top.time <= COALESCE_WINDOW_MS)
    ) {
      top.time = now;
      return;
    }
    this.undoStack.push({ label, project: before, coalesceKey, time: now });
    if (this.undoStack.length > MAX_HISTORY_ENTRIES) this.undoStack.shift();
  }

  /** Steps back, returning the earlier snapshot, or undefined when there is nothing to undo. */
  undo(current: Project): Project | undefined {
    const entry = this.undoStack.pop();
    if (!entry) return undefined;
    this.redoStack.push({ label: entry.label, project: current });
    this.endCoalescing();
    return entry.project;
  }

  /** Steps forward, returning the later snapshot, or undefined when there is nothing to redo. */
  redo(current: Project): Project | undefined {
    const entry = this.redoStack.pop();
    if (!entry) return undefined;
    this.undoStack.push({ label: entry.label, project: current, time: 0 });
    this.endCoalescing();
    return entry.project;
  }

  /** Whether there is a change to undo. */
  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /** Whether there is an undone change to redo. */
  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Label of the change that undo would revert. */
  undoLabel(): string | undefined {
    return this.undoStack[this.undoStack.length - 1]?.label;
  }

  /** Label of the change that redo would reapply. */
  redoLabel(): string | undefined {
    return this.redoStack[this.redoStack.length - 1]?.label;
  }

  /** Number of undo entries held. */
  get size(): number {
    return this.undoStack.length;
  }

  /** Discards all history. */
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
  }

  /** Stops the newest entry from absorbing further updates, for example after an undo. */
  private endCoalescing(): void {
    const top = this.undoStack[this.undoStack.length - 1];
    if (top) delete top.coalesceKey;
  }
}
